import { useEffect, useState, type ReactNode } from 'react'
import { NavLink, Navigate, Outlet, useNavigate } from 'react-router-dom'
import { sessaoAtual, type Sessao } from './dados/api.js'
import { useUsuarioAtual } from './dados/usuarioAtual.js'
import { iniciarSincronizacaoAutomatica } from './dados/sincronizacao.js'
import { AvisoAtualizacao } from './componentes/AvisoAtualizacao.js'
import { BarraSincronizacao } from './componentes/BarraSincronizacao.js'
import { BotaoAtualizar } from './componentes/BotaoAtualizar.js'

/// Casca do aplicativo: verifica a sessão guardada, liga a sincronização automática
/// e desenha o cabeçalho comum.
///
/// A sessão vale 30 dias e mora no dispositivo — é o que permite abrir o app numa
/// obra sem sinal e continuar trabalhando.

export function App() {
  const [sessao, setSessao] = useState<Sessao | null | undefined>(undefined)
  const { usuario } = useUsuarioAtual()
  const navegar = useNavigate()

  useEffect(() => {
    void sessaoAtual().then(setSessao)
  }, [])

  useEffect(() => {
    if (!sessao) return
    return iniciarSincronizacaoAutomatica()
  }, [sessao])

  if (sessao === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-slate-500">Carregando…</p>
      </div>
    )
  }
  if (sessao === null) return <Navigate to="/entrar" replace />

  const expirada = new Date(sessao.expiraEm) < new Date()

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-20 bg-marinho text-white shadow-md">
        <div className="largura flex items-center gap-3 py-3">
          <button
            onClick={() => navegar('/')}
            className="flex items-center gap-2 text-left"
            aria-label="Ir para o início"
          >
            <span className="grid h-8 w-8 place-items-center rounded-md bg-limao text-marinho-900">
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="3">
                <path d="M5 13l4 4 10-11" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
            <span>
              <span className="block text-sm font-bold leading-tight">QSMS</span>
              <span className="block text-[11px] leading-tight text-marinho-100">Construtora Ápia</span>
            </span>
          </button>
          <BotaoAtualizar />
          <div className="text-right">
            <p className="text-xs leading-tight text-marinho-100">{sessao.usuario.nome}</p>
            {usuario?.superAdmin ? (
              <span className="etiqueta bg-alta font-bold text-white">Super Admin</span>
            ) : (
              (usuario?.admin ?? sessao.usuario.admin) && (
                <span className="etiqueta bg-limao text-marinho-900">Admin</span>
              )
            )}
          </div>
        </div>
      </header>

      <AvisoAtualizacao />

      <Menu admin={usuario?.admin ?? sessao.usuario.admin} />

      {expirada && (
        <div className="bg-alta px-4 py-2 text-center text-sm text-white">
          Sua sessão expirou. Você pode continuar registrando, mas nada será enviado até entrar de novo.{' '}
          <button className="underline" onClick={() => navegar('/entrar')}>
            Entrar
          </button>
        </div>
      )}

      <BarraSincronizacao />

      <main className="largura pb-24 pt-4">
        <Outlet />
      </main>
    </div>
  )
}

/// Inspeções e Painel para todos; o Painel mostra só as obras de cada um. As telas
/// de administração só existem para admin, e só com rede: são outro trabalho,
/// feito de outro lugar — sentado, não em cima de um andaime.
function Menu({ admin }: { admin: boolean }) {
  return (
    <nav className="border-b border-slate-200 bg-white" aria-label="Menu">
      <div className="largura flex gap-1 overflow-x-auto">
        <ItemMenu para="/" fim>
          Inspeções
        </ItemMenu>
        {admin && <ItemMenu para="/checklists">Checklists</ItemMenu>}
        <ItemMenu para="/painel">Painel</ItemMenu>
        {admin && (
          <>
            <ItemMenu para="/criticidade">Criticidade Ápia</ItemMenu>
            <ItemMenu para="/usuarios">Usuários</ItemMenu>
            <ItemMenu para="/auditoria">Auditoria</ItemMenu>
          </>
        )}
      </div>
    </nav>
  )
}

function ItemMenu({ para, fim, children }: { para: string; fim?: boolean; children: ReactNode }) {
  return (
    <NavLink
      to={para}
      end={fim}
      className={({ isActive }) =>
        `whitespace-nowrap border-b-2 px-3 py-2.5 text-sm font-medium transition ${
          isActive
            ? 'border-marinho text-marinho'
            : 'border-transparent text-slate-500 hover:text-slate-800'
        }`
      }
    >
      {children}
    </NavLink>
  )
}
